import { ArrowUpRight, Check, FileText } from "lucide-react";
import { Card } from "@components/ui/card";
import { Badge } from "@shared-kernel/primary/shared/shadcn/ui/badge";
import { PageHeader } from "@shared-kernel/primary/shared/layout/shared-components/page-header";
import { PageWrapper } from "@shared-kernel/primary/shared/layout/shared-components/page-wrapper";
import type { DocumentaryResourceVM } from "@shared-kernel/domain/types/documentary-resource";
import type { DocumentaryResourceCategoryGroup } from "@shared-kernel/application/use-cases/documentary-resources/retrieval/group-resources-by-category";
import { formatDateToLocale } from "@utils/formatting";
import { EmptyState } from "@shared-kernel/primary/shared/layout/state-components/empty-state";

interface Props {
  groups: DocumentaryResourceCategoryGroup[];
  onOpen: (resourceId: string) => void;
  title?: string;
  subtitle?: string;
}

const ConsultationBadge = ({ lastConsultedAt }: { lastConsultedAt: DocumentaryResourceVM["lastConsultedAt"] }) => {
  if (!lastConsultedAt) {
    return (
      <Badge variant="outline" className="shrink-0 text-muted-foreground">
        Non lu
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="shrink-0 gap-1">
      <Check className="size-3" />
      Lu le {formatDateToLocale(lastConsultedAt)}
    </Badge>
  );
};

const ResourceCard = ({ resource, onOpen }: { resource: DocumentaryResourceVM; onOpen: (resourceId: string) => void }) => (
  <a
    href={resource.documentUrl}
    target="_blank"
    rel="noreferrer"
    onClick={() => onOpen(resource.id)}
    className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
  >
    <Card className="group relative flex h-full items-start gap-4 p-5 transition-all hover:-translate-y-0.5 hover:border-foreground/20 hover:shadow-md">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-muted/40 text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground">
        <FileText className="size-5" />
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-3">
          <h3 className="text-sm font-semibold leading-tight text-foreground">{resource.title}</h3>
          <ConsultationBadge lastConsultedAt={resource.lastConsultedAt} />
        </div>
        {resource.description && <p className="text-sm leading-relaxed text-muted-foreground">{resource.description}</p>}
      </div>
      <ArrowUpRight className="size-4 shrink-0 text-muted-foreground transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
    </Card>
  </a>
);

export const DocumentaryResourcesContent = ({ groups, onOpen, title = "Ressources", subtitle }: Props) => (
  <PageWrapper>
    <PageHeader title={title} subtitle={subtitle} />
    {groups.length === 0 ? (
      <EmptyState title="Aucune ressource disponible" description="Aucune ressource documentaire n'est disponible pour le moment." />
    ) : (
      <div className="space-y-8">
        {groups.map(group => (
          <section key={group.category} className="space-y-3">
            <div className="flex items-center gap-3">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{group.category}</h2>
              <div className="h-px flex-1 bg-border" />
            </div>
            <div className="grid gap-3">
              {group.resources.map(resource => (
                <ResourceCard key={resource.id} resource={resource} onOpen={onOpen} />
              ))}
            </div>
          </section>
        ))}
      </div>
    )}
  </PageWrapper>
);
